import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createColumnHelper } from "@tanstack/react-table"
import { apiClient } from "api"
import Button, { AppearanceTypes, SizeTypes } from "components/molecules/Button/Button"
import DataTable, { TableSizeTypes } from "components/organisms/DataTable/DataTable"
import { keys } from "lodash"
import { FC, SVGProps, useMemo, useState } from "react"
import { CAT2_API_BASE_URL } from "./constants"
import { useCatJobs, useDeleteCatJob } from "./useCatJobs"
import ExpandableContentContainer from "components/molecules/ExpandableContentContainer/ExpandableContentContainer"
import classes from "./classes.module.scss"
import { useTranslation } from "react-i18next"
import { ModalTypes, showModal, closeModal } from "components/organisms/modals/ModalRoot"
import { SourceFile } from "types/projects"
import dayjs from "dayjs"
import SimpleDropdown from "components/molecules/SimpleDropdown/SimpleDropdown"
import { downloadFile } from "helpers"
import { CattoJob, CattoJobStatus } from "./types"
import Delete from "assets/icons/delete.svg?react"
import classNames from "classnames"
import SmallTooltip from "components/molecules/SmallTooltip/SmallTooltip"
import ErrorIcon from "assets/icons/error.svg?react"
import BaseButton from "components/atoms/BaseButton/BaseButton"

const postAnalyses = async (params: any) => {
  return apiClient.post(`${CAT2_API_BASE_URL}/analyses`, params)
}

const postPretranslate = async (params: any) => {
  return apiClient.post(`${CAT2_API_BASE_URL}/jobs/pretranslate`, params)
}

type CatJobDownloadType = 'source' | 'xliff' | 'target'

const postJobDownload = async (params: { job_ids: string[]; type: CatJobDownloadType }) => {
  return apiClient.post(`${CAT2_API_BASE_URL}/download`, params, { responseType: 'blob' })
}

const getDownloadFileName = (
  jobs: CattoJob[] | undefined,
  jobId: string,
  type: CatJobDownloadType
) => {
  const job = jobs?.find((job) => job.id === jobId)
  switch (type) {
    case 'source':
      return job?.source_file?.file_name ?? `original_${jobId}`
    case 'xliff':
      return job?.xliff_file?.file_name ?? `${jobId}.xlf`
    case 'target':
      return job?.source_file?.file_name ?? `translated_${jobId}`
  }
}

const SpinnerIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" {...props}>
    <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
  </svg>
)

// ready jobs intentionally have no indicator
const jobStatusIndicators: Partial<
  Record<CattoJobStatus, { icon: FC<SVGProps<SVGSVGElement>>; className: string }>
> = {
  processing: { icon: SpinnerIcon, className: classes.jobStatusProcessing },
  pretranslating: { icon: SpinnerIcon, className: classes.jobStatusPretranslating },
  failed: { icon: ErrorIcon, className: classes.jobStatusFailed },
}

const columnHelper = createColumnHelper<any>()

interface JobsTableProps {
  catProjectId: string
  targetLocale?: string
  sourceFiles?: SourceFile[]
  // translator view: only opening in editor and downloading
  readOnly?: boolean
}

const JobsTable: FC<JobsTableProps> = (props) => {
  const { catProjectId, targetLocale, sourceFiles, readOnly } = props
  const { t } = useTranslation()
  const [rowSelection, setRowSelection] = useState({})
  const rowSelectionCount = useMemo(() => keys(rowSelection).length, [rowSelection])
  const queryClient = useQueryClient()

  const catJobsQuery = useCatJobs(catProjectId)
  const { mutateAsync: deleteJob } = useDeleteCatJob()

  const analyseMutation = useMutation({
    mutationFn: postAnalyses,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['catAnalyses'] })
      const analysisId = response?.data?.id
      if (analysisId) {
        showModal(ModalTypes.CatAnalysisDetails, { analysisId, catProjectId })
      }
    },
  })

  const pretranslateMutation = useMutation({
    mutationFn: postPretranslate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catJobs'] })
    },
  })

  const downloadMutation = useMutation({
    mutationFn: postJobDownload,
    onSuccess: (data, { job_ids, type, file_name }) => {
      
      const fileName =
        job_ids.length > 1
          ? 'files.zip'
          : getDownloadFileName(catJobsQuery.data?.data, job_ids[0], type)
      downloadFile({ data, fileName })
    },
  })

  const handleDelete = (jobId: string) => {
    showModal(ModalTypes.ConfirmationModal, {
      handleProceed: async () => {
        await deleteJob(jobId)
        closeModal()
      },
      modalContent: (
        <h1>{t('cat_tool_feature.jobs_table.delete_confirmation_text')}</h1>
      ),
    })
  }

  const jobTableColumns = [
    columnHelper.accessor('id', {
      header: '',
      footer: (info) => info.column.id,
      cell: ({ getValue, row }) => {
        return (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={(e) => row.toggleSelected(!!e.currentTarget.checked)}
          />
        )
      },
    }),
    columnHelper.accessor('source_file.file_name', {
      header: t('cat_tool_feature.jobs_table.column.file'),
    }),
    columnHelper.accessor('status', {
      header: '',
      cell: ({ getValue, row }) => {
        const status: CattoJobStatus = getValue()

        if (!(status in jobStatusIndicators)) {
          return null
        }

        const { icon, className } = jobStatusIndicators[status]!
        const label = t(`cat_tool_feature.jobs_table.status.${status}`)
        const error = status === 'failed' ? row.original.error : null

        return (
          <SmallTooltip
            className={classNames(classes.jobStatus, className)}
            icon={icon}
            ariaLabel={label}
            tooltipContent={error ? `${label}: ${error}` : label}
          />
        )
      }
    }),
    columnHelper.accessor('confirmed_percentage', {
      header: t('cat_tool_feature.jobs_table.column.confirmed_percentage'),
      cell: ({ row }) => {
        const segmentCount = catJobsQuery.data?.counts[row.original.id]?.segment_count
        const confirmedSegmentCount = catJobsQuery.data?.counts[row.original.id]?.confirmed_segment_count || 0

        if (!segmentCount) {
          return ''
        }

        const rawRatio = confirmedSegmentCount / segmentCount
        const roundedPercentage = Math.round((rawRatio + Number.EPSILON) * 1000) / 10


        return `${roundedPercentage}%`
      },
    }),
    columnHelper.accessor('created_at', {
      header: t('cat_tool_feature.jobs_table.column.created_at'),
      cell: ({ getValue }) => dayjs(getValue()).format('YYYY.MM.DD HH:mm'),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const translateUrl = catJobsQuery.data?.translate_urls[row.original.id]

        return (
          <div className={classes.actionsContainer}>
            <Button
              appearance={AppearanceTypes.Text}
              size={SizeTypes.S}
              href={translateUrl}
              target="_blank"
            >
              {t('cat_tool_feature.jobs_table.open_in_editor')}
            </Button>
            <BaseButton
              onClick={() => handleDelete(row.original.id)}
              aria-label={t('button.delete')}
              className={classes.iconButton}
              hidden={readOnly}
            >
              <Delete />
            </BaseButton>
          </div>
        )
      },
    }),
  ]

  return (
    <ExpandableContentContainer
      className={classes.expandableContainer}
      initialIsExpanded
      wrapContent
      leftComponent={
        <>
          <h3>{t('cat_tool_feature.jobs')}</h3>
        </>
      }
      rightComponent={
        <>
          <Button
            className={classes.mainButton}
            appearance={AppearanceTypes.Secondary}
            size={SizeTypes.S}
            disabled={rowSelectionCount == 0}
            hidden={readOnly}
            onClick={() => analyseMutation.mutate({ job_id: keys(rowSelection) })}
          >
            {t('cat_tool_feature.jobs_table.analyse')}
          </Button>
          <Button
            className={classes.mainButton}
            appearance={AppearanceTypes.Secondary}
            size={SizeTypes.S}
            disabled={rowSelectionCount == 0}
            hidden={readOnly}
            onClick={() => pretranslateMutation.mutate({ job_ids: keys(rowSelection) })}
          >
            {t('cat_tool_feature.jobs_table.pretranslate')}
          </Button>
          <SimpleDropdown
            className={classes.mainButton}
            appearance={AppearanceTypes.Secondary}
            icon={null}
            size={SizeTypes.S}
            label={t('cat_tool_feature.jobs_table.download')}
            disabled={rowSelectionCount == 0}
            options={[
              {
                label: t('cat_tool_feature.jobs_table.download_original'),
                onClick: () =>
                  downloadMutation.mutate({ job_ids: keys(rowSelection), type: 'source' }),
              },
              {
                label: t('cat_tool_feature.jobs_table.download_xliff'),
                onClick: () =>
                  downloadMutation.mutate({ job_ids: keys(rowSelection), type: 'xliff' }),
              },
              {
                label: t('cat_tool_feature.jobs_table.download_translated'),
                onClick: () =>
                  downloadMutation.mutate({ job_ids: keys(rowSelection), type: 'target' }),
              },
            ]}
          />
          <Button
            className={classes.mainButton}
            size={SizeTypes.S}
            hidden={readOnly}
            onClick={() =>
              showModal(ModalTypes.AddCatJobFiles, {
                catProjectId,
                targetLocale,
                sourceFiles,
              })
            }
          >
            {t('cat_tool_feature.jobs_table.add_files')}
          </Button>
        </>
      }
    >
      <DataTable
        data={catJobsQuery.data?.data || []}
        columns={jobTableColumns}
        tableSize={TableSizeTypes.M}
        getRowId={row => row.id}
        className={classes.translationMemoriesTable}
        hidePagination
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
      />
    </ExpandableContentContainer>
  )
}

export default JobsTable